import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Upload, Smartphone, Shield, CheckCircle2, AlertTriangle, Clock, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const StaffAppTab = ({ BACKEND_URL }) => {
  const [latestVersion, setLatestVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [version, setVersion] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState('');

  const downloadUrl = `${BACKEND_URL}/api/staff-app/download`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [latestRes, versionsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/staff-app/latest`),
        fetch(`${BACKEND_URL}/api/admin/staff-app/versions`),
      ]);
      if (latestRes.ok) setLatestVersion(await latestRes.json());
      if (versionsRes.ok) setVersions(await versionsRes.json());
    } catch (e) {
      console.error('Failed to load staff app data:', e);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async () => {
    if (!uploadFile || !version) return;
    setUploading(true);
    setUploadResult('');
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('version', version);
    formData.append('release_notes', releaseNotes);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/staff-app/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setUploadResult(data.message);
      setUploadFile(null);
      setVersion('');
      setReleaseNotes('');
      setShowUpload(false);
      fetchData();
    } catch (e) {
      setUploadResult('Error: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const hasApk = latestVersion?.available;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="staff-app-tab">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[#1a4d2e] flex items-center gap-2">
            <Smartphone className="w-5 h-5" /> Arvo Staff App
          </h2>
          <p className="text-sm text-gray-500">Download and install the field data collection app</p>
        </div>
        <button
          onClick={() => { setShowUpload(!showUpload); setUploadResult(''); }}
          className={`flex items-center gap-1 px-4 py-2 text-sm rounded-lg font-medium transition-colors ${showUpload ? 'bg-gray-200 text-gray-700' : 'bg-[#1a4d2e] text-white hover:bg-[#2d6b47]'}`}
          data-testid="upload-apk-btn"
        >
          <Upload className="w-4 h-4" /> {showUpload ? 'Cancel' : 'Upload New APK'}
        </button>
      </div>

      {uploadResult && (
        <div className={`rounded-lg p-3 mb-4 text-sm font-medium ${uploadResult.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {uploadResult}
        </div>
      )}

      {/* Upload Form */}
      {showUpload && (
        <div className="bg-white rounded-lg border-2 border-[#1a4d2e]/20 p-6 mb-6" data-testid="upload-apk-form">
          <h3 className="font-semibold text-[#1a4d2e] mb-4">Upload New APK Version</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version Number</label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. 1.0.0"
                data-testid="apk-version-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">APK File</label>
              <input
                type="file"
                accept=".apk"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#1a4d2e] file:text-white hover:file:bg-[#2d6b47]"
                data-testid="apk-file-input"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Release Notes (optional)</label>
            <textarea
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              rows={2}
              placeholder="What's new in this version..."
              data-testid="apk-notes-input"
            />
          </div>
          <button
            onClick={handleUpload}
            disabled={!uploadFile || !version || uploading}
            className="w-full bg-[#ff6b00] text-white py-3 rounded-lg font-semibold hover:bg-[#ff6b00]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="apk-upload-submit"
          >
            {uploading ? 'Uploading...' : 'Upload APK'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : (
        <>
          {/* Download Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
            <div className="bg-[#1a4d2e] text-white p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Smartphone className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Arvo</h3>
                  <p className="text-white/80 text-sm">Field Data Collection App</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {hasApk ? (
                <div className="flex flex-col md:flex-row gap-6 items-center">
                  {/* Download Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="font-semibold text-[#1a4d2e]">Version {latestVersion?.version || '1.0.0'}</span>
                      {latestVersion?.file_size_mb && (
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{latestVersion.file_size_mb} MB</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Last updated: {latestVersion?.uploaded_at ? new Date(latestVersion.uploaded_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Unknown'}</span>
                    </div>
                    {latestVersion?.release_notes && (
                      <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{latestVersion.release_notes}</p>
                    )}
                    <a
                      href={downloadUrl}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#ff6b00] text-white rounded-lg font-semibold hover:bg-[#ff6b00]/90 transition-colors shadow-md"
                      data-testid="download-apk-btn"
                    >
                      <Download className="w-5 h-5" /> Download APK
                    </a>
                  </div>

                  {/* QR Code */}
                  <div className="text-center" data-testid="qr-code-section">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-4 inline-block shadow-sm">
                      <QRCodeSVG
                        value={downloadUrl}
                        size={160}
                        level="M"
                        fgColor="#1a4d2e"
                        includeMargin={false}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 max-w-[180px]">Scan with your phone camera to download directly</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Smartphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No APK uploaded yet</p>
                  <p className="text-sm text-gray-400 mt-1">Use the "Upload New APK" button above to add the first version</p>
                </div>
              )}
            </div>
          </div>

          {/* Install Instructions */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-testid="install-instructions">
            <h3 className="font-semibold text-[#1a4d2e] mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" /> Installation Instructions
            </h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1a4d2e] text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                <div>
                  <p className="font-medium text-gray-800">Download the APK</p>
                  <p className="text-sm text-gray-500">Tap the download button above or scan the QR code with your phone's camera. The APK file will download to your device.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1a4d2e] text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                <div>
                  <p className="font-medium text-gray-800">Enable Unknown Sources</p>
                  <p className="text-sm text-gray-500">When prompted, you'll need to allow installation from unknown sources:</p>
                  <ul className="text-sm text-gray-500 mt-1 space-y-0.5 ml-4">
                    <li>Go to <strong>Settings &gt; Security</strong> (or <strong>Settings &gt; Apps &gt; Special Access</strong>)</li>
                    <li>Enable <strong>"Install unknown apps"</strong> for your browser (Chrome/Samsung Internet)</li>
                    <li>On newer Android, you'll be prompted automatically when opening the APK</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1a4d2e] text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                <div>
                  <p className="font-medium text-gray-800">Install the App</p>
                  <p className="text-sm text-gray-500">Open the downloaded APK file and tap <strong>"Install"</strong>. If you see a Play Protect warning, tap <strong>"Install anyway"</strong> — the app is safe, it's just not from the Play Store.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#ff6b00] text-white flex items-center justify-center text-sm font-bold shrink-0">4</div>
                <div>
                  <p className="font-medium text-gray-800">Open Arvo & Sign In</p>
                  <p className="text-sm text-gray-500">Find the <strong>Arvo</strong> app in your app drawer and open it. Sign in with your staff credentials to start collecting field data.</p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Updating to a New Version</p>
                  <p className="text-sm text-amber-700 mt-1">When a new version is available, simply download and install again. Android will update the existing app and keep your data.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Version History */}
          {versions.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                data-testid="version-history-toggle"
              >
                <h3 className="font-semibold text-[#1a4d2e] flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Version History ({versions.length})
                </h3>
                {showHistory ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>
              {showHistory && (
                <div className="border-t border-gray-100 divide-y divide-gray-100">
                  {versions.map((v, idx) => (
                    <div key={idx} className="p-4 flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold ${v.is_latest ? 'bg-[#1a4d2e]' : 'bg-gray-400'}`}>
                        {v.is_latest ? 'NEW' : 'OLD'}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-800">
                          v{v.version}
                          {v.is_latest && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Latest</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          {v.uploaded_at ? new Date(v.uploaded_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                          {v.file_size_mb ? ` — ${v.file_size_mb} MB` : ''}
                        </p>
                        {v.release_notes && <p className="text-xs text-gray-500 mt-1">{v.release_notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StaffAppTab;
